// CourseCard.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './CourseCard.css';
import { PiBookOpenTextThin } from "react-icons/pi";
import { GoClock } from "react-icons/go";
import { CiCalendar } from "react-icons/ci";

interface Course {
  id: number;
  image: string;
  imageClass?: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  price: string;
  title: string;
  description: string;
  authorLabel: string;
  authorLocation: string;
  progressFilled?: number; // out of 10 (only for my-courses)
  progressText?: string; // (only for my-courses)
  completedCount?: number; // (only for my-courses)
  totalCount?: number;
  durationMinutes?: number; // total minutes spent (for my-courses) or total course duration (for browse/wishlist)
  dueDate?: string; // expected format "dd-mm-yyyy" (only for my-courses)
  totalModules?: number; // total modules (for browse/wishlist)
  totalHours?: number; // total hours (for browse/wishlist)
  isCompleted?: boolean; // for showing certificate download (only for my-courses)
  displayMode: 'my-courses' | 'browse' | 'wishlist'; // display mode
  userRole?: 'trainee' | 'trainer'; // to determine navigation route
}

// Function to get CSS class based on level
const getLevelClass = (level: string): string => {
  const levelLower = level.toLowerCase();
  if (levelLower === 'beginner') return 'log-in-home-beginner';
  if (levelLower === 'intermediate') return 'log-in-home-intermediate';
  if (levelLower === 'advanced') return 'log-in-home-advanced';
  return 'log-in-home-beginner'; // default
};

const formatDuration = (minutes: number): string => {
  if (minutes < 60) return `${minutes} mins Spent`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hrs} Hours Spent`;
  return `${hrs}h ${mins}m Spent`;
};

const formatDate = (dateStr: string): string => {
  // expecting "dd-mm-yyyy"
  const [dayStr, monthStr, yearStr] = dateStr.split('-');
  const day = Number(dayStr);
  const month = Number(monthStr);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthName = monthNames[month - 1] ?? '';
  return `${day} ${monthName}`;
};

interface CourseCardProps {
  course: Course;
  isSaved?: boolean;
  onToggleSave?: (e: React.MouseEvent) => void;
  onEnroll?: (e: React.MouseEvent) => void;
}

const CourseCard: React.FC<CourseCardProps> = ({
  course,
  isSaved = false,
  onToggleSave,
  onEnroll,
}) => {
  const navigate = useNavigate();
  const isMyCourses = course.displayMode === 'my-courses';
  const isBrowseOrWishlist = course.displayMode === 'browse' || course.displayMode === 'wishlist';

  // Format total duration for browse/wishlist mode
  const formatTotalDuration = (hours?: number): string => {
    if (!hours) return '0 hr';
    const hrs = Math.floor(hours);
    const mins = Math.round((hours - hrs) * 60);
    if (mins === 0) return `${hrs} hr`;
    return `${hrs} hr ${mins} mins`;
  };

  const handleCardClick = () => {
    // Determine the route based on user role
    const baseRoute = course.userRole === 'trainer' ? '/trainer' : '/trainee';

    // Determine source label based on displayMode for breadcrumb
    let sourceLabel = 'My Courses'; // default
    if (course.displayMode === 'browse') {
      sourceLabel = 'Explore Courses';
    } else if (course.displayMode === 'wishlist') {
      sourceLabel = 'Wishlist';
    }

    navigate(`${baseRoute}/course/${course.id}`, {
      state: { sourceLabel }
    });
  };

  return (
    <div className="log-in-home-course-card" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
      <div className={`log-in-home-course-image ${course.imageClass ?? ''}`}>
        <img src={course.image} alt={course.title} />
      </div>
      <div className="log-in-home-course-content">
        <div className="log-in-home-course-header">
          <span className={`log-in-home-course-level ${getLevelClass(course.level)}`}>{course.level}</span>
          <span className="log-in-home-course-price">{course.price}</span>
        </div>
        <h3>{course.title}</h3>
        <p>{course.description}</p>
        <div className="log-in-home-course-author">
          <div className="log-in-home-author-avatar">{course.authorLabel}</div>
          <div className="log-in-home-author-info">
            <span>{course.authorLocation}</span>
            {isMyCourses && course.isCompleted && (
              <span className="log-in-home-certificate-text">Download Certification</span>
            )}
          </div>

          {/* Browse mode: Show save and enroll buttons */}
          {course.displayMode === 'browse' && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                className="log-in-home-continue-btn"
                onClick={onEnroll}
                style={{
                  flex: 1,
                  backgroundColor: 'var(--primary-color, #5966A8)',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Enroll Now
              </button>
              <button
                onClick={onToggleSave}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isSaved ? 'var(--primary-color, #5966A8)' : 'transparent',
                  color: isSaved ? 'white' : 'var(--primary-color, #5966A8)',
                  border: `1px solid ${isSaved ? 'var(--primary-color, #5966A8)' : 'var(--color-border, #E5E7EB)'}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                }}
                title={isSaved ? 'Remove from wishlist' : 'Add to wishlist'}
              >
                {isSaved ? '✓ Saved' : 'Save'}
              </button>
            </div>
          )}

          {/* Wishlist mode: Show save button only */}
          {course.displayMode === 'wishlist' && (
            <button
              className="log-in-home-continue-btn"
              onClick={onToggleSave}
              style={{
                backgroundColor: 'transparent',
                color: 'var(--primary-color, #5966A8)',
                border: '1px solid var(--primary-color, #5966A8)',
              }}
            >
              Remove from Wishlist
            </button>
          )}

          {/* My Courses mode: Show continue button */}
          {isMyCourses && (
            <button className="log-in-home-continue-btn">
              Continue
            </button>
          )}
        </div>

        {/* Progress blocks for My Courses */}
        {isMyCourses && (
          <div className="log-in-home-course-progress">
            <div className="log-in-home-progress-dots">
              {[...Array(10)].map((_, i) => {
                const isFilled = i < (course.progressFilled ?? 0);
                const filledClass =
                  (course.progressFilled ?? 0) <= 5
                    ? 'log-in-home-filled'
                    : 'log-in-home-filled log-in-home-green';
                return (
                  <div
                    key={i}
                    className={`log-in-home-dot ${
                      isFilled ? filledClass : ''
                    }`}
                  ></div>
                );
              })}
            </div>
            <span className="log-in-home-progress-text">{course.progressText}</span>
          </div>
        )}

        {/* Info for Browse/Wishlist */}
        {isBrowseOrWishlist && (
          <div className="log-in-home-course-info">
            <span className="log-in-home-course-info-item">
              <span className="log-in-home-course-stat-icon"><PiBookOpenTextThin /></span>
              <span>{course.totalModules ?? 0} modules</span>
            </span>
            <span className="log-in-home-course-info-item">
              <span className="log-in-home-course-stat-icon"><GoClock /></span>
              <span>{formatTotalDuration(course.totalHours)}</span>
            </span>
          </div>
        )}

        {/* Stats for My Courses */}
        {isMyCourses && (
          <div className="log-in-home-course-stats">
            <span className="log-in-home-course-stat">
              <span className="log-in-home-course-stat-icon"><PiBookOpenTextThin /></span>
              <span>{`${course.completedCount ?? 0}/${course.totalCount ?? 0} Completed`}</span>
            </span>
            <span className="log-in-home-course-stat">
              <span className="log-in-home-course-stat-icon"><GoClock /></span>
              <span>{formatDuration(course.durationMinutes ?? 0)}</span>
            </span>
            {course.dueDate && (
              <span className="log-in-home-course-stat">
                <span className="log-in-home-course-stat-icon"><CiCalendar /></span>
                <span>Due: {formatDate(course.dueDate)}</span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseCard;